import { ShipBuilderState, CrewComposition } from "../../types/ship";
import { CalculationResult } from "../../types/calculations";
import { DriveType } from "../../types/drive";

/**
 * Calculates crew requirements based on ship configuration
 * @param state - The ship builder state
 * @returns Calculation result with crew composition or error
 */
export const calculateCrewRequirements = (
  state: ShipBuilderState,
): CalculationResult<CrewComposition> => {
  try {
    const shipSize = state.shipSize || 100;
    const hasJumpDrive =
      state.drives?.some((d) => d.type === DriveType.JUMP) || false;
    const hasWeapons =
      state.weaponSelections && Object.keys(state.weaponSelections).length > 0;
    // const isMilitary = state.finalDetails?.isMilitary || false;

    // Base crew requirements
    // Pilot: 1 plus 1 for each small craft (Traveller rules)
    let pilots = 1 + (state.smallCraftCount || 0);
    let astrogators = hasJumpDrive ? 1 : 0;
    // Engineer calculation per clarified Traveller rules:
    // 1 per 35 tons of drives and power plant (main ship only), plus 1 per small craft on board
    let totalDriveTonnage = 0;
    if (state.drives && state.drives.length > 0) {
      for (const drive of state.drives) {
        totalDriveTonnage += require("../../constants/drives").getDriveTons(
          drive.type,
          drive.rating,
          shipSize,
        );
      }
    }
    if (state.powerPlant && typeof state.powerPlant.weight === "number") {
      totalDriveTonnage += state.powerPlant.weight;
    }
    // Use smallCraftCount from the new user-defined small craft system
    let engineers = Math.ceil(totalDriveTonnage / 35) + (state.smallCraftCount || 0);
    engineers = Math.max(engineers, 1);
    let gunners = hasWeapons
      ? Math.ceil(Object.keys(state.weaponSelections || {}).length / 2)
      : 0;
    let stewards = 0; // Stewards are now calculated based on passengers, not ship size
    let medics = Math.ceil(shipSize / 2000); // 1 medic per 2000 tons

    return {
      success: true,
      data: {
        captain: 1, // Always need a captain
        pilot: pilots,
        astrogator: astrogators,
        engineer: engineers,
        maintenance: Math.ceil(shipSize / 1000), // 1 per 1000 tons
        gunner: gunners,
        steward: stewards,
        administrator: Math.ceil(shipSize / 2000), // 1 per 2000 tons
        sensorOperator: Math.ceil(shipSize / 7500), // 1 per 7500 tons
        medic: medics,
        officer: Math.ceil(
          (pilots + astrogators + engineers + gunners + stewards + medics) / 20,
        ), // 1 per 20 crew
        marines: 0, // Ship's Troops/Marines
      },
    };
  } catch (error) {
    return {
      success: false,
      error: {
        type: "CALCULATION_ERROR",
        message: `Failed to calculate crew requirements: ${error instanceof Error ? error.message : "Unknown error"}`,
      },
    };
  }
};
